import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import {
  CandidatePersonalDetailsFormActionsService,
  CandidatePersonalDetailsFormDataService,
} from '@core/candidate-logic';
import { CandidateDetailsForm, FullCandidate } from '@core/candidate-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { GeneralTitleValue, ProfessionalTitleValue } from '@core/salutation-fields-types';
import {
  ButtonComponent,
  FormFieldComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  ProtectedControlDirective,
  RtTooltip,
  SelectComponent,
} from '@core/user-interface-legacy';
import { WalkthroughStepOriginDirective } from '@core/walkthrough-ui';
import { WalkthroughSalutationFieldsService } from '@core/walkthroughs-legacy';

@Component({
  selector: 'rt-candidate-personal-details-form',
  imports: [
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    TranslatePipe,
    SelectComponent,
    ProtectedControlDirective,
    IconComponent,
    IconButtonComponent,
    RtTooltip,
    ButtonComponent,
    InputComponent,
    WalkthroughStepOriginDirective,
    NgTemplateOutlet,
  ],
  templateUrl: './candidate-personal-details-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './candidate-personal-details-form.component.less',
})
export class CandidatePersonalDetailsFormComponent {
  private readonly dataService = inject(CandidatePersonalDetailsFormDataService);
  private readonly actionsService = inject(CandidatePersonalDetailsFormActionsService);
  private readonly walkthroughSalutationFieldsService = inject(WalkthroughSalutationFieldsService);
  private readonly i18nService = inject(I18nService);

  public detailsForm = input.required<CandidateDetailsForm>();
  public candidate = input<FullCandidate>();
  public autoFocusOnName = input(false);

  protected hasCandidateSalutationFieldsAvailable =
    this.dataService.hasCandidateSalutationFieldsAvailable;
  protected professionalTitleOptions = this.dataService.professionalTitleOptions;
  protected generalTitleOptions = this.dataService.generalTitleOptions;

  protected hasSalutation = computed(
    () =>
      !!(this.candidate()?.salutation && this.candidate()?.salutation !== GeneralTitleValue.None),
  );
  protected hasTitle = computed(
    () => !!(this.candidate()?.title && this.candidate()?.title !== ProfessionalTitleValue.None),
  );
  protected showSalutationFields = computed(() => {
    if (!this.hasCandidateSalutationFieldsAvailable()) return false;
    const currentShowSalutationFields = this.currentShowSalutationFields();
    if (currentShowSalutationFields !== null) {
      return currentShowSalutationFields;
    }
    const hasCandidateSalutationFields = this.hasSalutation() || this.hasTitle();
    return hasCandidateSalutationFields || this.showSalutationFieldsUsed();
  });
  protected showAddTitlesButton = computed(
    () => this.hasCandidateSalutationFieldsAvailable() && !this.showSalutationFields(),
  );
  protected generalTitleOptionsWithCannyLink = computed(() => [
    ...this.generalTitleOptions(),
    {
      value: 'missing_link',
      label: this.i18nService.translate(
        'app.candidate.edit_candidate_personal_details.missing_title',
      ),
    },
  ]);
  protected professionalTitleOptionsWithCannyLink = computed(() => [
    ...this.professionalTitleOptions(),
    {
      value: 'missing_link',
      label: this.i18nService.translate(
        'app.candidate.edit_candidate_personal_details.missing_title',
      ),
    },
  ]);

  private currentShowSalutationFields = signal<boolean | null>(null);

  private showSalutationFieldsUsed = this.dataService.showSalutationFieldsUsed;

  protected ngOnInit() {
    this.walkthroughSalutationFieldsService.start(3);
  }

  protected addSalutationFields(): void {
    this.currentShowSalutationFields.set(true);
    this.actionsService.setShowSalutationFieldsUsed(true);
  }

  protected hideSalutationFields(): void {
    this.currentShowSalutationFields.set(false);
    this.actionsService.setShowSalutationFieldsUsed(false);
  }

  protected openCanny(): void {
    const link = this.i18nService.translate(
      'app.application_form.candidate_information.salutation_fields.feedback_link',
    );
    window.open(link, '_blank');
  }
}
